import { getMessaging, getToken } from 'firebase/messaging';
import { app } from '../../config/firebase';
import { db } from '../../config/firebase';
import { doc, setDoc, collection, query, where, getDocs, serverTimestamp } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { Capacitor } from '@capacitor/core';
// @ts-ignore
import { PushNotifications } from '@capacitor/push-notifications';

class PushNotificationService {
  private readonly vapidKey = 'BHGABWwb-9JcDmfjAqU9-owsCcLSUfDrN0yDpj0LLu_vzR_s_NWUgO1xHBWDGHwRwG9Biq42oK-_rcRw0DEofWU'; // À remplacer par votre clé VAPID
  private fallbackMode = false;
  private notificationPermissionGranted = false;
  private nativePushToken: string | null = null;

  constructor() {
    // Vérifier si les notifications sont supportées
    this.notificationPermissionGranted = 'Notification' in window && Notification.permission === 'granted';
  }

  async requestPermission(): Promise<boolean> {
    if (Capacitor.isNativePlatform()) {
      const permStatus = await PushNotifications.requestPermissions();
      this.notificationPermissionGranted = permStatus.receive === 'granted';
      return this.notificationPermissionGranted;
    }

    if (!('Notification' in window)) {
      console.warn('[DEBUG] Ce navigateur ne supporte pas les notifications push');
      this.fallbackMode = true;
      return false;
    }

    try {
      const permission = await Notification.requestPermission();
      this.notificationPermissionGranted = permission === 'granted';
      return this.notificationPermissionGranted;
    } catch (error) {
      console.error('[DEBUG] Erreur lors de la demande de permission:', error);
      this.fallbackMode = true;
      return false;
    }
  }

  async registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
    if (!('serviceWorker' in navigator)) {
      console.warn('[DEBUG] Service Worker non supporté');
      this.fallbackMode = true;
      return null;
    }

    try {
      const registration = await navigator.serviceWorker.register('/service-worker.js');
      // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // console.log('[DEBUG] Service Worker enregistré avec succès');
      return registration;
    } catch (error) {
      console.error('[DEBUG] Erreur lors de l\'enregistrement du Service Worker:', error);
      this.fallbackMode = true;
      return null;
    }
  }

  async getFirebaseToken(): Promise<string | null> {
    if (Capacitor.isNativePlatform()) {
      return this.nativePushToken;
    }

    // Si nous sommes déjà en mode de repli, ne pas essayer d'obtenir un token
    if (this.fallbackMode) {
      // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // console.log('[DEBUG] Mode de repli actif, pas de tentative de récupération de token');
      return null;
    }

    try {
      // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // console.log('[DEBUG] Début de récupération du token Firebase');
      
      // Vérifier que le service worker est enregistré
      if (!('serviceWorker' in navigator)) {
        console.error('[DEBUG] Service Worker non supporté par ce navigateur');
        this.fallbackMode = true;
        return null;
      }
      
      // Vérifier que les notifications sont autorisées
      if (Notification.permission !== 'granted') {
        console.warn('[DEBUG] Les notifications ne sont pas autorisées');
        const permission = await this.requestPermission();
        if (!permission) {
          console.error('[DEBUG] Permission de notification refusée');
          this.fallbackMode = true;
          return null;
        }
      }
      
      // Vérifier l'enregistrement du service worker
      try {
        const registration = await navigator.serviceWorker.getRegistration('/firebase-messaging-sw.js');
        if (!registration) {
          // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // console.log('[DEBUG] Service worker Firebase non trouvé, tentative d\'enregistrement');
          try {
            await navigator.serviceWorker.register('/firebase-messaging-sw.js');
          } catch (regError) {
            console.error('[DEBUG] Échec de l\'enregistrement du service worker Firebase:', regError);
            this.fallbackMode = true;
            return null;
          }
        }
      } catch (swError) {
        console.error('[DEBUG] Erreur lors de la vérification/enregistrement du service worker:', swError);
        this.fallbackMode = true;
        return null;
      }
      
      // Attendre que le service worker soit prêt
      try {
        await navigator.serviceWorker.ready;
        // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // console.log('[DEBUG] Service worker prêt');
      } catch (readyError) {
        console.error('[DEBUG] Erreur lors de l\'attente du service worker:', readyError);
        this.fallbackMode = true;
        return null;
      }
      
      // Récupérer le token
      try {
        const messaging = getMessaging(app);
        // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // console.log('[DEBUG] Tentative de récupération du token avec la clé VAPID:', this.vapidKey.substring(0, 10) + '...');
        
        const registration = await navigator.serviceWorker.getRegistration('/firebase-messaging-sw.js');
        const tokenOptions: { vapidKey: string; serviceWorkerRegistration?: ServiceWorkerRegistration } = {
          vapidKey: this.vapidKey
        };
        if (registration) {
          tokenOptions.serviceWorkerRegistration = registration;
        }
        const token = await getToken(messaging, tokenOptions);
        
        if (token) {
          // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // console.log('[DEBUG] Token Firebase récupéré avec succès:', token.substring(0, 10) + '...');
          return token;
        } else {
          console.error('[DEBUG] Aucun token n\'a été récupéré');
          this.fallbackMode = true;
          return null;
        }
      } catch (tokenError) {
        console.error('[DEBUG] Erreur lors de la récupération du token:', tokenError);
        this.fallbackMode = true;
        return null;
      }
    } catch (error) {
      console.error('[DEBUG] Erreur détaillée lors de la récupération du token Firebase:', error);
      
      // Vérifier le type d'erreur pour donner des conseils plus précis
      const err = error as Error;
      if (err.name === 'AbortError' && err.message.includes('push service error')) {
        console.error('[DEBUG] Erreur du service push. Vérifiez que:');
        console.error('1. Le service worker firebase-messaging-sw.js est correctement configuré');
        console.error('2. La clé VAPID est correcte et correspond à votre projet Firebase');
        console.error('3. Le navigateur est compatible avec les notifications push');
        console.error('4. Le domaine est sécurisé (HTTPS) ou localhost');
      }
      
      this.fallbackMode = true;
      return null;
    }
  }

  async setupPushNotifications(): Promise<boolean> {
    try {
      // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // console.log('[DEBUG] Début de la configuration des notifications push');
      
      // Si nous sommes déjà en mode de repli, retourner false
      if (this.fallbackMode) {
        // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // console.log('[DEBUG] Mode de repli actif, pas de configuration des notifications push');
        return false;
      }

      if (Capacitor.isNativePlatform()) {
        let permStatus = await PushNotifications.checkPermissions();
        if (permStatus.receive === 'prompt') {
          permStatus = await PushNotifications.requestPermissions();
        }
        if (permStatus.receive !== 'granted') {
          return false;
        }
        
        await PushNotifications.addListener('registration', (token: any) => {
          this.nativePushToken = token.value;
        });
        
        await PushNotifications.addListener('pushNotificationReceived', (notification: any) => {});

        await PushNotifications.register();
        // Attendre un peu que le token soit récupéré
        await new Promise(resolve => setTimeout(resolve, 1000));
        return true;
      }
      
      // Vérifier la compatibilité du navigateur
      if (!('Notification' in window)) {
        console.error('[DEBUG] Ce navigateur ne supporte pas les notifications');
        this.fallbackMode = true;
        return false;
      }
      
      if (!('serviceWorker' in navigator)) {
        console.error('[DEBUG] Ce navigateur ne supporte pas les Service Workers');
        this.fallbackMode = true;
        return false;
      }
      
      // Demander la permission si nécessaire
      if (Notification.permission !== 'granted') {
        // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // console.log('[DEBUG] Demande de permission pour les notifications');
        const permissionGranted = await this.requestPermission();
        if (!permissionGranted) {
          console.warn('[DEBUG] Permission de notification refusée par l\'utilisateur');
          this.fallbackMode = true;
          return false;
        }
      } else {
        // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // console.log('[DEBUG] Permission de notification déjà accordée');
      }
      
      // Enregistrer le service worker principal
      try {
        await navigator.serviceWorker.register('/service-worker.js', {
          scope: '/'
        });
        // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // console.log('[DEBUG] Service Worker principal enregistré avec succès');
      } catch (error) {
        console.error('[DEBUG] Erreur lors de l\'enregistrement du Service Worker principal:', error);
        // Continuer même si l'enregistrement du service worker principal échoue
      }
      
      // Enregistrer le service worker Firebase
      try {
        await navigator.serviceWorker.register('/firebase-messaging-sw.js', {
          scope: '/firebase-cloud-messaging-push-scope'
        });
        // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // console.log('[DEBUG] Service Worker Firebase enregistré avec succès');
      } catch (error) {
        console.error('[DEBUG] Erreur lors de l\'enregistrement du Service Worker Firebase:', error);
        this.fallbackMode = true;
        return false;
      }
      
      // Attendre que le service worker soit activé
      await navigator.serviceWorker.ready;
      // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // console.log('[DEBUG] Service Worker activé');
      
      // Récupérer le token Firebase
      const token = await this.getFirebaseToken();
      if (!token) {
        console.error('[DEBUG] Impossible de récupérer le token Firebase');
        this.fallbackMode = true;
        return false;
      }
      
      // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // console.log('[DEBUG] Configuration des notifications push terminée avec succès');
      return true;
    } catch (error) {
      console.error('[DEBUG] Erreur lors de la configuration des notifications push:', error);
      this.fallbackMode = true;
      return false;
    }
  }

  async subscribeToPushNotifications(userId: string): Promise<boolean> {
    // Si nous sommes en mode de repli, stocker cette information dans Firestore
    if (this.fallbackMode) {
      // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // console.log('[DEBUG] Mode de repli actif, enregistrement de l\'utilisateur sans token');
      try {
        const userPrefRef = doc(collection(db, 'user_preferences'), userId);
        await setDoc(userPrefRef, {
          pushNotificationsEnabled: this.notificationPermissionGranted,
          pushNotificationsFallback: true,
          updatedAt: serverTimestamp()
        }, { merge: true });
        return this.notificationPermissionGranted;
      } catch (error) {
        console.error('[DEBUG] Erreur lors de l\'enregistrement des préférences en mode de repli:', error);
        return false;
      }
    }

    const token = await this.getFirebaseToken();
    if (!token) {
      this.fallbackMode = true;
      return this.subscribeToPushNotifications(userId); // Réessayer en mode de repli
    }

    try {
      // Stocker le token directement dans Firestore au lieu d'utiliser un endpoint API
      const tokenRef = doc(collection(db, 'push_tokens'), token);
      await setDoc(tokenRef, {
        userId,
        token,
        createdAt: serverTimestamp(),
        platform: 'web',
        active: true
      });
      
      // Mettre à jour les préférences de l'utilisateur
      const userPrefRef = doc(collection(db, 'user_preferences'), userId);
      await setDoc(userPrefRef, {
        pushNotificationsEnabled: true,
        pushNotificationsFallback: false,
        updatedAt: serverTimestamp()
      }, { merge: true });
      
      // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // console.log('[DEBUG] Token de notification enregistré avec succès');
      return true;
    } catch (error) {
      console.error('[DEBUG] Erreur lors de l\'abonnement aux notifications:', error);
      this.fallbackMode = true;
      return this.subscribeToPushNotifications(userId); // Réessayer en mode de repli
    }
  }

  async unsubscribeFromPushNotifications(userId: string): Promise<boolean> {
    try {
      // Mettre à jour les préférences de l'utilisateur
      const userPrefRef = doc(collection(db, 'user_preferences'), userId);
      await setDoc(userPrefRef, {
        pushNotificationsEnabled: false,
        updatedAt: serverTimestamp()
      }, { merge: true });
      
      // Si nous sommes en mode de repli, c'est tout ce que nous avons à faire
      if (this.fallbackMode) {
        // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // console.log('[DEBUG] Mode de repli actif, désabonnement simplifié');
        return true;
      }
      
      // Sinon, procéder au désabonnement complet
      const token = await this.getFirebaseToken();
      if (token) {
        // Mettre à jour le statut du token dans Firestore
        const tokenRef = doc(collection(db, 'push_tokens'), token);
        await setDoc(tokenRef, { active: false, updatedAt: serverTimestamp() }, { merge: true });
      }
      
      // Désabonner le navigateur
      try {
        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.getSubscription();
        if (subscription) {
          await subscription.unsubscribe();
        }
      } catch (error) {
        console.error('[DEBUG] Erreur lors de la désinscription du navigateur:', error);
        // Continuer même en cas d'erreur
      }
      
      return true;
    } catch (error) {
      console.error('[DEBUG] Erreur lors de la désinscription aux notifications:', error);
      return false;
    }
  }

  async diagnosticPushNotifications(): Promise<{ status: string, details: Record<string, any> }> {
    const diagnostic = {
      status: 'unknown',
      details: {
        browser: {
          supportsNotifications: 'Notification' in window,
          supportsServiceWorker: 'serviceWorker' in navigator,
          notificationPermission: Notification.permission
        },
        serviceWorkers: {
          registered: [] as string[],
          firebase: null as any
        },
        firebase: {
          tokenAvailable: false,
          tokenPrefix: '',
          fallbackMode: this.fallbackMode
        },
        errors: [] as string[]
      }
    };

    try {
      // Vérifier les service workers enregistrés
      if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        diagnostic.details.serviceWorkers.registered = registrations.map(reg => reg.scope);
        
        // Vérifier spécifiquement le service worker Firebase
        const firebaseRegistration = await navigator.serviceWorker.getRegistration('/firebase-messaging-sw.js');
        diagnostic.details.serviceWorkers.firebase = firebaseRegistration 
          ? { scope: firebaseRegistration.scope, active: !!firebaseRegistration.active }
          : null;
      }

      // Essayer de récupérer un token Firebase
      try {
        // Sauvegarder l'état du mode de repli
        const originalFallbackMode = this.fallbackMode;
        this.fallbackMode = false; // Désactiver temporairement le mode de repli pour tester
        
        const token = await this.getFirebaseToken();
        diagnostic.details.firebase.tokenAvailable = !!token;
        if (token) {
          diagnostic.details.firebase.tokenPrefix = token.substring(0, 10) + '...';
        }
        
        // Restaurer l'état du mode de repli
        this.fallbackMode = originalFallbackMode;
      } catch (error: any) {
        diagnostic.details.errors.push(`Erreur de token: ${error.message}`);
      }

      // Déterminer le statut global
      if (this.fallbackMode) {
        diagnostic.status = 'fallback_mode';
      } else if (!diagnostic.details.browser.supportsNotifications || !diagnostic.details.browser.supportsServiceWorker) {
        diagnostic.status = 'unsupported';
      } else if (diagnostic.details.browser.notificationPermission !== 'granted') {
        diagnostic.status = 'permission_required';
      } else if (!diagnostic.details.serviceWorkers.firebase) {
        diagnostic.status = 'service_worker_missing';
      } else if (!diagnostic.details.firebase.tokenAvailable) {
        diagnostic.status = 'token_unavailable';
      } else {
        diagnostic.status = 'operational';
      }

      return diagnostic;
    } catch (error: any) {
      diagnostic.status = 'error';
      diagnostic.details.errors.push(`Erreur de diagnostic: ${error.message}`);
      return diagnostic;
    }
  }

  async sendPushNotification(userId: string, notification: { title: string, body: string, data?: any }): Promise<boolean> {
    try {
      // Si nous sommes en mode de repli, vérifier si l'utilisateur a activé les notifications
      if (this.fallbackMode) {
        // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // console.log('[DEBUG] Mode de repli actif, vérification des préférences utilisateur');
        
        // Vérifier les préférences de l'utilisateur
        const userPrefDoc = await getDocs(query(collection(db, 'user_preferences'), where('__name__', '==', userId)));
        
        if (userPrefDoc.empty || !userPrefDoc.docs[0]?.data()?.['pushNotificationsEnabled']) {
          // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // console.log('[DEBUG] Utilisateur non abonné aux notifications ou en mode de repli');
          return false;
        }
        
        // En mode de repli, nous stockons simplement la notification dans Firestore
        // pour qu'elle soit affichée lors de la prochaine connexion de l'utilisateur
        // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // console.log('[DEBUG] Stockage de la notification en mode de repli');
        
        // La notification sera affichée via l'interface utilisateur lors de la prochaine connexion
        return true;
      }
      
      // Récupérer les tokens de l'utilisateur
      const tokensQuery = query(
        collection(db, 'push_tokens'),
        where('userId', '==', userId),
        where('active', '==', true)
      );
      
      const tokensSnapshot = await getDocs(tokensQuery);
      const tokens = tokensSnapshot.docs.map(doc => doc.data()['token']);
      
      if (tokens.length === 0) {
        // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // console.log('[DEBUG] Aucun token trouvé pour l\'utilisateur:', userId);
        return false;
      }
      
      // Utiliser Firebase Cloud Functions pour envoyer la notification
      try {
        const functions = getFunctions(app);
        const sendPushNotification = httpsCallable(functions, 'sendPushNotification');
        
        await sendPushNotification({
          tokens,
          notification: {
            title: notification.title,
            body: notification.body
          },
          data: notification.data || {}
        });
        
        // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // console.log('[DEBUG] Notification push envoyée avec succès');
        return true;
      } catch (error) {
        console.error('[DEBUG] Erreur lors de l\'envoi de la notification push via Cloud Functions:', error);
        // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // console.log('[DEBUG] Les notifications push nécessitent une fonction Cloud déployée.');
        
        // Passer en mode de repli pour les futures notifications
        this.fallbackMode = true;
        return false;
      }
    } catch (error) {
      console.error('[DEBUG] Erreur lors de l\'envoi de la notification push:', error);
      this.fallbackMode = true;
      return false;
    }
  }
  
  // Méthode pour vérifier si les notifications sont en mode de repli
  isFallbackMode(): boolean {
    return this.fallbackMode;
  }
  
  // Méthode pour forcer le mode de repli (utile pour les tests ou les environnements problématiques)
  setFallbackMode(enabled: boolean): void {
    this.fallbackMode = enabled;
    // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // // console.log(`[DEBUG] Mode de repli ${enabled ? 'activé' : 'désactivé'} manuellement`);
  }
}

export const pushNotificationService = new PushNotificationService(); 